import { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { pubsubActions } from '@redux'
import pubSubEventHandler from './eventHandler'
import { storageHelpers } from '@utils'

const WEB_PUBSUB_PROTOCOLS = ['json.webpubsub.azure.v1']

class PubSubService {
    constructor() {
        this.socket = null
        this.connectionStatus = 'disconnected' // 'disconnected'|'connecting'|'connected'|'error'
        this.connectionError = null
        this.listeners = []
        this.reconnectAttempts = 0
        this.maxReconnectAttempts = 5
        this.reconnectInterval = 3000
        this.negotiateEndpoint = process.env.REACT_APP_NEGOTIATE_ENDPOINT
        this.connectionStatusListeners = []
        this.reconnectTimeout = null

        // Track joined groups locally
        this._groups = new Set()
        this._groupStatusListeners = []
        // Track "desired" groups (what the app intends to be in). Rejoin these after reconnect
        this._desiredGroups = new Set()
    }

    getNegotiateEndpoint() {
        return this.negotiateEndpoint
    }
    setNegotiateEndpoint(endpoint) {
        this.negotiateEndpoint = endpoint
    }
    getConnectionStatus() {
        return this.connectionStatus
    }
    getConnectionError() {
        return this.connectionError
    }

    addConnectionStatusListener(listener) {
        this.connectionStatusListeners.push(listener)
        return () => {
            this.connectionStatusListeners = this.connectionStatusListeners.filter(l => l !== listener)
        }
    }

    _updateConnectionStatus(status, error = null) {
        this.connectionStatus = status
        this.connectionError = error
        this.connectionStatusListeners.forEach(l => l(status, error))
    }

    async _getNegotiateUrl() {
        const res = await fetch(this.negotiateEndpoint)
        if (!res.ok) throw new Error(`Failed to negotiate: ${res.status} ${res.statusText}`)
        const data = await res.json()
        return data.url
    }

    async connect() {
        // console.log('Attempting to connect WebSocket, current state:', this.socket ? this.socket.readyState : 'no socket')

        if (this.socket && (this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.OPEN)) {
            // console.log('WebSocket is already connecting or connected')
            return
        }

        if (this.socket && (this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.OPEN)) {
            // console.log('WebSocket is already connecting or connected')
            return
        }
        this._updateConnectionStatus('connecting')
        try {
            const url = await this._getNegotiateUrl()
            this.socket = new WebSocket(url, WEB_PUBSUB_PROTOCOLS)
            this.socket.onopen = this._handleOpen.bind(this)
            this.socket.onmessage = this._handleMessage.bind(this)
            this.socket.onclose = this._handleClose.bind(this)
            this.socket.onerror = this._handleError.bind(this)
            storageHelpers.setConnectionUrl(url)
            this.reconnectAttempts = 0

            console.log('WebSocket connection established')
        } catch (err) {
            this._updateConnectionStatus('error', err)
            this._scheduleReconnect()
        }
    }

    disconnect() {
        if (!this.socket) return
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout)
            this.reconnectTimeout = null
        }
        this.socket.close()
        this.socket = null
        this._updateConnectionStatus('disconnected')

        // Clear joined groups on disconnect and notify
        if (this._groups.size) {
            this._groups.clear()
            this._notifyGroupStatus()
        }
    }

    _scheduleReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) return
        this.reconnectAttempts++
        const delay = this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1)
        this.reconnectTimeout = setTimeout(() => this.connect(), delay)
    }

    _handleOpen() {
        // console.log('WebSocket connection established')
        this._updateConnectionStatus('connected')
        this.reconnectAttempts = 0

        // Attempt to rejoin desired groups after reconnect
        if (this._desiredGroups.size) {
            const groupsToRejoin = Array.from(this._desiredGroups)
            // Rejoin sequentially to keep ordering stable
            groupsToRejoin.reduce((p, g) => p.then(() => this.joinGroup(g)), Promise.resolve())
        }
    }

    _handleMessage(event) {
        try {
            const data = JSON.parse(event.data)

            this.listeners.forEach(l => {
                if (l.type === data.type || l.type === '*') l.callback(data)
            })
        } catch (e) {
            console.error('Error parsing message:', e)
        }
    }

    _handleClose(event) {
        // console.log(`WebSocket closed: ${event.code} ${event.reason}`)
        this._updateConnectionStatus('disconnected')
        if (event.code !== 1000) this._scheduleReconnect()
    }

    _handleError() {
        this._updateConnectionStatus('error', new Error('WebSocket connection error'))
    }

    addEventListener(type, callback) {
        const listener = { type, callback }
        this.listeners.push(listener)
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener)
        }
    }

    // ----- Group status helpers -----
    _notifyGroupStatus() {
        const groups = Array.from(this._groups)
        this._groupStatusListeners.forEach(fn => fn(groups))
    }

    addGroupStatusListener(listener) {
        this._groupStatusListeners.push(listener)
        // Immediately emit current state
        listener(Array.from(this._groups))
        return () => {
            this._groupStatusListeners = this._groupStatusListeners.filter(l => l !== listener)
        }
    }

    getGroups() {
        return Array.from(this._groups)
    }

    isInGroup(group) {
        return this._groups.has(group)
    }

    /**
     * Internal send helper
     * @param {Object} payload - JSON message to send
     * @returns {void}
     * @private
     */
    _send(payload) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            const err = new Error('WebSocket is not connected')
            throw err
        }

        // Debug log for outbound message
        // console.log('[PubSub SEND]', {
        //     type: payload.type,
        //     group: payload.group,
        //     dataType: payload.dataType,
        //     hasData: typeof payload.data !== 'undefined',
        // })
        this.socket.send(JSON.stringify(payload))
    }

    /**
     * Wait until the WebSocket connection is established or error/timeout occurs
     * @param {number} timeoutMs default 10000ms
     * @returns {Promise<void>}
     */
    waitUntilConnected(timeoutMs = 10000) {
        if (this.connectionStatus === 'connected') return Promise.resolve()
        if (this.connectionStatus === 'error') return Promise.reject(this.connectionError || new Error('connection error'))

        return new Promise((resolve, reject) => {
            const start = Date.now()
            const off = this.addConnectionStatusListener((s, e) => {
                if (s === 'connected') {
                    off()
                    resolve()
                } else if (s === 'error') {
                    off()
                    reject(e || new Error('connection error'))
                } else if (Date.now() - start > timeoutMs) {
                    off()
                    reject(new Error('Timed out waiting for connection'))
                }
            })
        })
    }

    /**
     * Join an Azure Web PubSub group
     * @param {string} group - Group name
     * @returns {void}
     */
    joinGroup(group) {
        const payload = { type: 'joinGroup', group }
        this._send(payload)
        this._desiredGroups.add(group)

        // Update local group state immediately (optimistic update)
        if (!this._groups.has(group)) {
            this._groups.add(group)
            this._notifyGroupStatus()
        }
    }

    /**
     * Leave an Azure Web PubSub group
     * @param {string} group - Group name
     * @returns {void}
     */
    leaveGroup(group) {
        const payload = { type: 'leaveGroup', group }
        this._send(payload)
        if (this._desiredGroups.has(group)) this._desiredGroups.delete(group)

        // Update local group state immediately (optimistic update)
        if (this._groups.has(group)) {
            this._groups.delete(group)
            this._notifyGroupStatus()
        }
    }

    /**
     * Send a message to an Azure Web PubSub group
     * @param {string} group - Group name
     * @param {any} data - Data to send (string or JSON-serializable)
     * @param {Object} [options]
     * @param {'text'|'json'|'binary'} [options.dataType] - Defaults inferred from data
     * @param {boolean} [options.noEcho] - Avoid echoing to sender
     * @returns {void}
     */
    sendToGroup(group, data, options = {}) {
        const { dataType, noEcho } = options
        let resolvedType = dataType
        let payloadData = data

        if (!resolvedType) {
            if (typeof data === 'string') {
                resolvedType = 'text'
            } else if (data instanceof Uint8Array) {
                resolvedType = 'binary'
            } else {
                resolvedType = 'json'
            }
        }

        if (resolvedType === 'json' && typeof data !== 'string') {
            // Allow objects, arrays, numbers, etc.
            payloadData = data
        }

        const payload = {
            type: 'sendToGroup',
            group,
            data: payloadData,
            dataType: resolvedType,
            ...(typeof noEcho === 'boolean' ? { noEcho } : {}),
        }

        this._send(payload)
    }
}

export const usePubSub = (options = {}) => {
    const { groupName } = options || {}
    const [connectionStatus, setConnectionStatus] = useState(pubSubService.getConnectionStatus())
    const [connectionError, setConnectionError] = useState(pubSubService.getConnectionError())
    const [groups, setGroups] = useState(pubSubService.getGroups())

    useEffect(() => {
        const removeListener = pubSubService.addConnectionStatusListener((s, e) => {
            setConnectionStatus(s)
            setConnectionError(e)
        })
        const removeGroups = pubSubService.addGroupStatusListener(next => setGroups(next))

        // Clean up listener on unmount
        return () => {
            removeListener()
            removeGroups()
        }
    }, [])

    const connect = useCallback(() => pubSubService.connect(), [])
    const waitUntilConnected = useCallback(timeoutMs => pubSubService.waitUntilConnected(timeoutMs), [])
    const disconnect = useCallback(() => pubSubService.disconnect(), [])
    const addEventListener = useCallback((type, cb) => pubSubService.addEventListener(type, cb), [])

    // Group operations
    const joinGroup = useCallback(
        (group, opts) => {
            const g = group ?? groupName
            if (!g) throw new Error('joinGroup requires a group name (provide arg or options.groupName)')
            return pubSubService.joinGroup(g, opts)
        },
        [groupName]
    )

    const leaveGroup = useCallback(
        (group, opts) => {
            const g = group ?? groupName
            if (!g) throw new Error('leaveGroup requires a group name (provide arg or options.groupName)')
            return pubSubService.leaveGroup(g, opts)
        },
        [groupName]
    )

    const sendToGroup = useCallback(
        (group, data, opts) => {
            const g = group ?? groupName
            if (!g) throw new Error('sendToGroup requires a group name (provide arg or options.groupName)')
            return pubSubService.sendToGroup(g, data, opts)
        },
        [groupName]
    )

    const isInGroup = useCallback(group => pubSubService.isInGroup(group), [])

    return {
        connectionStatus,
        connectionError,
        connect,
        waitUntilConnected,
        disconnect,
        addEventListener,
        isConnected: connectionStatus === 'connected',
        isConnecting: connectionStatus === 'connecting',
        hasError: connectionStatus === 'error',
        // group helpers
        joinGroup,
        leaveGroup,
        sendToGroup,
        isInGroup,
        groups,
    }
}

/**
 * PubSubInitializer component
 * Initializes the PubSub event handler with Redux dispatch
 * Use this component inside a Redux Provider
 */
export const PubSubInitializer = ({ children }) => {
    const dispatch = useDispatch()

    useEffect(() => {
        // Initialize the PubSub event handler with dispatch
        if (pubSubEventHandler) {
            pubSubEventHandler.initialize(dispatch)
        }

        // Bridge PubSubService connection status to Redux pubsub slice
        const removeStatusListener = pubSubService.addConnectionStatusListener((status, error) => {
            dispatch(pubsubActions.setStatus(status))
            if (error) dispatch(pubsubActions.setError(error.message || String(error)))
        })

        // Auto-connect WebSocket on app init
        pubSubService.connect()

        // Clean up when the component is unmounted
        return () => {
            if (pubSubEventHandler) {
                pubSubEventHandler.cleanup()
            }
            if (removeStatusListener) removeStatusListener()
            pubSubService.disconnect()
        }
    }, [dispatch, pubSubEventHandler])

    // Return children if provided, otherwise null
    return children || null
}

const pubSubService = new PubSubService()
export default pubSubService
